import { useEffect, useState } from 'react';
import axios from 'axios';
import { Wrench, Power, CheckCircle2, Rocket } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR') : '—');

const ADIMLAR = [
  ['Bakımı AÇ', 'Kullanıcılar "Site bakım aşamasındadır" ekranını görür; yeni kayıt/düzenleme sunucuda da reddedilir.'],
  ['Deploy et', 'Emergent\'ta "Republish changes" ile yeni sürümü canlıya al (10-15 dk).'],
  ['Kontrol et', 'Yönetici olarak siteyi aç, kritik ekranları hızlıca dene (bakım açıkken admin her şeyi kullanabilir).'],
  ['Bakımı KAPAT', 'Kullanıcı ekranları 20 sn içinde otomatik açılır; herkes kaldığı yerden devam eder.'],
];

export const BakimModuPanel = ({ onChange }) => {
  const [state, setState] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/settings/maintenance`);
      setState(res.data); setMessage(res.data.message || '');
    } catch (e) { flash('❌ Durum yüklenemedi: ' + (e.response?.data?.detail || e.message)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (enabled) => {
    if (enabled && !window.confirm('Bakım modu AÇILSIN mı?\n\nTüm kullanıcılar (yöneticiler hariç) siteyi kullanamayacak ve veri girişi yapamayacak.')) return;
    if (!enabled && !window.confirm('Bakım modu KAPATILSIN mı? Kullanıcılar siteyi tekrar kullanabilecek.')) return;
    setBusy(true);
    try {
      const res = await axios.put(`${API}/settings/maintenance`, { enabled, message }, { withCredentials: true });
      setState(res.data); onChange?.(res.data.enabled);
      flash(enabled ? '🔧 Bakım modu AÇILDI — kullanıcılar bakım ekranını görüyor' : '✅ Bakım modu KAPATILDI — site tekrar kullanımda');
    } catch (e) { flash('❌ Kaydedilemedi: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const saveMessage = async () => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/settings/maintenance`, { enabled: !!state?.enabled, message }, { withCredentials: true });
      setState(res.data); flash('✅ Bakım mesajı kaydedildi');
    } catch (e) { flash('❌ Kaydedilemedi: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (!state) return <p className="text-gray-400">Bakım modu durumu yükleniyor…</p>;
  const on = !!state.enabled;

  return (
    <div className="space-y-6" data-testid="bakim-modu-panel">
      {msg && <div className="p-3 bg-gray-800 border border-amber-700/50 rounded-lg text-sm text-white" data-testid="bakim-panel-msg">{msg}</div>}

      <Card className={`border ${on ? 'bg-amber-950/30 border-amber-600/60' : 'bg-gray-900 border-gray-800'}`} data-testid="bakim-toggle-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Wrench className={`w-5 h-5 ${on ? 'text-amber-400' : 'text-gray-500'}`} /> Site Bakım Modu
                <Badge className={on ? 'bg-amber-600 text-black' : 'bg-gray-700 text-gray-200'} data-testid="bakim-durum-badge">{on ? 'AÇIK' : 'KAPALI'}</Badge>
              </CardTitle>
              <CardDescription className="text-gray-400 mt-1">
                {on ? 'Kullanıcılar şu anda "Site bakım aşamasındadır" ekranını görüyor; yazma istekleri sunucuda reddediliyor. Yöneticiler etkilenmez.' : 'Deploy öncesi açın: kullanıcılar yarım kalan veri girişi yapamaz, deploy bitince kapatın.'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Label htmlFor="bakim-switch" className={`text-sm font-semibold ${on ? 'text-amber-300' : 'text-gray-400'}`}>{on ? 'ON' : 'OFF'}</Label>
              <Switch id="bakim-switch" checked={on} disabled={busy} onCheckedChange={save} className="data-[state=checked]:bg-amber-500 scale-125" data-testid="bakim-switch" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-gray-800/60 border border-gray-700"><p className="text-xs text-gray-500">Başlangıç</p><p className="text-white" data-testid="bakim-baslangic">{on ? fmt(state.started_at) : '—'}</p></div>
            <div className="p-3 rounded-lg bg-gray-800/60 border border-gray-700"><p className="text-xs text-gray-500">Son değişiklik</p><p className="text-white">{fmt(state.updated_at)}</p></div>
            <div className="p-3 rounded-lg bg-gray-800/60 border border-gray-700"><p className="text-xs text-gray-500">Değiştiren</p><p className="text-white" data-testid="bakim-degistiren">{state.updated_by || '—'}</p></div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Kullanıcılara gösterilecek mesaj</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="bg-gray-800 border-gray-700 text-white" data-testid="bakim-mesaj-input" />
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={saveMessage} disabled={busy || message === state.message} className="border-gray-700 text-gray-200 hover:bg-gray-800" data-testid="bakim-mesaj-kaydet-btn">Mesajı Kaydet</Button>
            </div>
          </div>
          <Button onClick={() => save(!on)} disabled={busy} className={`w-full sm:w-auto ${on ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`} data-testid="bakim-toggle-btn">
            <Power className="w-4 h-4 mr-2" /> {on ? 'Bakımı Kapat (Siteyi Aç)' : 'Bakımı Aç (Siteyi Kapat)'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><Rocket className="w-4 h-4 text-blue-400" /> Güvenli Deploy Akışı</CardTitle>
          <CardDescription className="text-gray-400">Her sunucu güncellemesinde bu sırayı izleyin.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3">
            {ADIMLAR.map(([baslik, aciklama], i) => (
              <li key={baslik} className="flex gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-gray-800 border border-gray-700 text-gray-300 text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                <div><p className="text-white font-medium">{baslik}</p><p className="text-gray-400">{aciklama}</p></div>
              </li>
            ))}
          </ol>
          <div className="mt-4 flex items-start gap-2 text-xs text-gray-500"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Bakım durumu veritabanında saklanır; deploy sırasında sunucu yeniden başlasa bile korunur.</div>
        </CardContent>
      </Card>
    </div>
  );
};
